import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { canHaveCustomProperties } from './canHaveCustomProperties'

function isAxiosRequestConfig(thing: unknown): thing is AxiosRequestConfig {
  return canHaveCustomProperties(thing)
    && (typeof thing.url === 'string' || typeof thing.url === 'undefined')
    && (typeof thing.method === 'string' || typeof thing.method === 'undefined')
}

function isAxiosResponse(thing: unknown): thing is AxiosResponse {
  return canHaveCustomProperties(thing)
    && typeof thing.data !== 'undefined'
    && typeof thing.status === 'number'
    && typeof thing.statusText === 'string'
    && isAxiosRequestConfig(thing.config)
}

/**
 * A custom type guard for {@link AxiosError} (see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards)
 * The primary intention of this function is to test whether a caught error was a well-formed {@link AxiosError} object
 * @deprecated Use `axios.isAxiosError(error)` instead
 * @param thing The thing which is being type-guarded
 * @returns Whether the thing is an {@link AxiosError}
 * @example
 * try {
 *   await axios.get('/username')
 * } catch (e: unknown) {
 *   // not the expected AxiosError so re-throw it
 *   if (!isAxiosError(e)) throw e
 *   // `e` holds the type `AxiosError`; assuming there were no other axios runtime errors, this is the expected path of this example code
 *   console.log(e.response?.status)
 *   console.log(e.code)
 *   console.log(e.config.url)
 * }
 * @example
 * try {
 *   const person: any = {}
 *   console.log(person.name.firstLetter.alphabeticalIndex) // this will throw an error
 *   await axios.get('/username')
 * } catch (e: unknown) {
 *   // not the expected AxiosError so re-throw it
 *   // this is the expected code path because of the runtime error produced in the `try` block
 *   if (!isAxiosError(e)) throw e
 *   // so this path would not be followed, but it would be valid TypeScript because of the type guard
 *   console.log(e.response?.status)
 *   console.log(e.code)
 *   console.log(e.config.url)
 * }
 */
export function isAxiosError(thing: unknown): thing is AxiosError {
  return canHaveCustomProperties(thing)
    && isAxiosRequestConfig(thing.config)
    && (typeof thing.code === 'undefined' || typeof thing.code === 'string')
    && (typeof thing.response === 'undefined' || isAxiosResponse(thing.response))
}
